/**
 * Datalähteiden selain-silta (AB-WP4.1). Portaa fasthub-agentin
 * desktop-bridge-turvamallin: sandbox-iframe EI pidä tokenia — se postaa
 * pyynnön PARENT-ikkunalle, joka pitää käyttäjän Bearer-tokenin ja tekee
 * varsinaisen kutsun (serving-host BFF:ää vasten, editorin esikatselu
 * Mocks-paneelin datasta).
 *
 * Viestisopimus:
 *   pyyntö : { type: 'fasthub:app:invoke',  id, sourceId, payload, query }
 *   vastaus: { type: 'fasthub:app:response', id, ok, result, error }
 *            result = { statusCode, responseBody, durationMs }
 *
 * `payload` menee request-runkoon (bodyllisille metodeille), `query` URL:n
 * query-parametreiksi (taulun request-parametrit joilla in=query, AB-WP4.1).
 *
 * `sourceId` on komponentin `data-app-api-id` (julkaistun Appin deklaroitu
 * datalähde). iframe ei koskaan nimeä reittiä/upstreamia — parent+palvelin
 * resolvoivat sen Appin descriptorista. Serving-shellissä (meta olemassa)
 * silta on self-invoke myös kehystettynä (WP-EM3); ilman metaa ja parentia
 * silta on `null` ja verkkopolut no-oppaavat siististi.
 */

import {
  AppDocument, documentBlobType, ensureExtension, isViewableInTab, parseAppDocument,
  safeFilename, textBlobType, type TextExportFormat,
} from './appDocument';
import { embedderTargets, parseEmbedOrigins, type EmbedderMessage } from './embedderMessage';
import { sanitizeUrl } from './url';

export interface InvokeResult {
  statusCode: number;
  responseBody: string;
  durationMs: number;
}

/** `user-actions`-komponentin sillan yli kulkeva vastaus (WP-UA4). */
export interface UserActionAnswerPayload {
  selectedOption?: string;
  selectedOptions?: string[];
  comment?: string;
}

/**
 * Sillan virhe joka tarkoittaa "tässä kontekstissa ei ole katsojaa"
 * (julkinen standalone-serving, päätös `D4` = A). Runtime näyttää tästä
 * kirjautumisohjeen eikä virhettä — ero on käyttäjälle olennainen.
 */
export const NO_IDENTITY = 'fasthub:no-identity';

export interface BridgeClient {
  invoke(
    sourceId: string,
    payload?: unknown,
    query?: Record<string, unknown>,
  ): Promise<InvokeResult>;

  /**
   * Katsojalle osoitetut käyttäjätoimet (WP-UA4).
   *
   * <p><b>Ei datalähde vaan sillan oma operaatio.</b> Toimet ovat alustan dataa:
   * palvelin päättää kohdennuksesta katsojan identiteetin perusteella, eikä App
   * voi nimetä reittiä jolta ne haetaan. Sama luottamusraja kuin
   * `currentUser`-siemenellä — iframe ei kanna tokenia eikä tiedä API-polkua.
   */
  listUserActions(queue?: string, includeResolved?: boolean): Promise<unknown[]>;

  /** Vastaa yhteen käyttäjätoimeen; palauttaa ratkaistun toimen palvelimelta. */
  respondUserAction(id: string, answer: UserActionAnswerPayload): Promise<unknown>;
  /**
   * Avaa ulkoinen linkki (WP-AR5, `D8` = B).
   *
   * <p>Miksi sillan yli eikä suoraan: julkaistu App ajetaan
   * `sandbox="allow-scripts"`-iframessa ILMAN `allow-popups`-lupaa, joten
   * `window.open` on selaimen estämä siellä. Sandboxin löysääminen olisi
   * avannut juuri sen rajan jota vastaan koko srcdoc-malli on rakennettu, joten
   * iframe nimeää aikeen ja parent tekee sen — sama luottamusmalli kuin
   * datalähteillä. **Parent sanitoi URLin**; iframe ei voi luottaa siihen että
   * mikä tahansa skeema avautuu.
   *
   * <p>Fire-and-forget: avaus ei palauta mitään eikä sitä voi peruuttaa, joten
   * korrelaatio-id:tä ei tarvita.
   */
  openLink(url: string, target: 'new' | 'same'): void;

  /**
   * Näytä datalähteen palauttama dokumentti (WP-KU3, `D1` = A).
   *
   * <p>Miksi sillan yli: Appin CSP on `default-src 'none'` ILMAN
   * `frame-src`/`object-src`, joten `<iframe>`/`<embed>`/`<object>` on estetty
   * myös `data:`- ja `blob:`-URL:eilla — PDF:ää ei yksinkertaisesti voi
   * renderöidä Appin sisällä. Portaalissa on jo kovetettu katselin
   * (`DocPreviewModal`: sivunavigointi, latausnappi, MIME:n uudelleenkääritys),
   * joten iframe nimeää aikeen ja parent näyttää.
   *
   * <p><b>Appi ei koskaan nimeä URL:ää</b> — vain datalähteen id:n, jonka
   * palvelin on jo allowlistannut Appin omasta descriptorista. Se on sama
   * raja kuin `invoke`lla, eikä uutta luottamuspintaa synny.
   *
   * <p>Fire-and-forget kuten {@link openLink}: virheen näyttää parent, koska
   * se omistaa katselimen.
   */
  openDocument(sourceId: string, payload?: unknown): void;

  /**
   * Tallenna datalähteen palauttama dokumentti katsojan koneelle
   * (WP-KU3, `D2` = B).
   *
   * <p>Erillinen operaatio esikatselun rinnalla, koska tiedostoja käsitellään
   * erissä: "avaa katselin → klikkaa lataa → sulje katselin" kertautuu kymmeniä
   * kertoja. Sandboxissa lataus ei onnistu millään tavalla — `allow-downloads`
   * puuttuu — joten tämäkin kulkee parentin kautta.
   */
  downloadDocument(sourceId: string, payload?: unknown): void;

  /**
   * Tallenna Apin **itse tuottama** tekstisisältö katsojan koneelle
   * (data-gridin CSV-vienti).
   *
   * <p><b>Poikkeaa {@link downloadDocument}ista luottamusluokaltaan.</b> Siellä
   * iframe nimeää vain datalähteen id:n, jonka palvelin on jo allowlistannut
   * Appin descriptorista, ja parent hakee tavut omalla tokenillaan. Tässä
   * tavuja ei ole missään palvelimella: ne syntyvät Appin omassa muistissa,
   * ja iframe antaa SISÄLLÖN. Parent ei siksi luota mihinkään kentistä — se
   * siivoaa nimen, pakottaa päätteen, rajaa koon ja johtaa sisältötyypin
   * `format`ista (ks. `bridgeHost.ts`).
   *
   * <p>Nimi on `save-text` eikä `save-csv` tarkoituksella: operaatio on
   * "iframe antaa itse tuottamansa tavut", ei "data-grid vie CSV:n".
   * Mekanismin mukaan nimetty operaatio pitää luottamuseron luettavana ja
   * antaa tuleville vienneille (JSON/ICS) yhden kovetetun polun kahden sijaan.
   *
   * <p>Fire-and-forget kuten {@link openLink}: parent omistaa latauksen.
   */
  saveText(filename: string, content: string, format: TextExportFormat): void;

  /**
   * Ilmoita <b>upottavalle sivulle</b> tapahtumasta (`notify-embedder`,
   * upotussivun-tapahtumaviesti-plan.md WP-UV2).
   *
   * <p>Ei sillan protokollaa vaan viesti vieraalle sivulle: vastaanottaja on
   * asiakkaan oma sivu (esim. sen Tag Manager), ei FastHub. Siksi viesti lähtee
   * vain serving-tilassa ja vain kehystettynä, ja vain Appin sallituille
   * upotusoriginille (`fasthub-embed-origins`-meta, D1 = A): ensisijaisesti
   * selaimen ilmoittamalle todelliselle upottajalle, muuten koko listalle, jolloin
   * selain toimittaa täsmäävälle (`embedderTargets`). Ei koskaan listaamattomalle
   * eikä `'*'`:lle. Portaalin runnerissa ja esikatselussa no-op (D4 = A); kortti
   * näkyy silti Debug-aikajanassa.
   *
   * <p>Viesti on jo rakennettu ja tarkistettu (`buildEmbedderMessage`).
   * Fire-and-forget kuten {@link openLink}.
   */
  notifyEmbedder(message: EmbedderMessage): void;
}

interface Pending {
  resolve(r: unknown): void;
  reject(e: Error): void;
  timer: ReturnType<typeof setTimeout>;
}

const REQUEST_TIMEOUT_MS = 15_000;

/**
 * Valitse silta-asiakas ympäristön mukaan — **ajotapa ensin, kehystys vasta
 * sitten** (app-builder-upotus-plan.md WP-EM3, `D3` = A):
 *   - **Serving** (shellissä on {@code <meta name="fasthub-app-id">}):
 *     self-invoke suoraan julkiseen datapolkuun
 *     ({@code POST /apps/{id}/invoke}, same-origin). Meta emittoituu VAIN
 *     standalone-servingissä (AppRenderer), joten se on tarkka merkki siitä
 *     että self-invoke on oikea kanava — myös vieraan sivuston `iframe`ssa,
 *     jossa parent ei ole FastHub eikä vastaisi postMessageen koskaan.
 *     Palvelin sallii vain julkisen Appin + julkisen (authMode=none) reitin.
 *   - **Kehystetty** (parent ≠ self, ei metaa): postMessage-silta parentille
 *     (editorin esikatselu / portaalin {@code /app/:id}-runner, joka pitää
 *     käyttäjän tokenin ja resolvoi kutsun). Runnerin ja esikatselun
 *     dokumenteissa metaa ei ole (servingAppId = null), joten niiden käytös
 *     ei muutu tästä järjestyksestä.
 *   - Muuten null (esim. kehystämätön esikatselu ilman serving-id:tä) — kutsuja
 *     no-oppaa.
 */
export function createBridgeClient(win: Window = window): BridgeClient | null {
  const appId = readServingAppId(win.document);
  if (appId) {
    return createServingClient(appId, win);
  }
  if (win.parent !== win) {
    return createFramedClient(win);
  }
  return null;
}

/**
 * Lue standalone-servingin app-id shellin metasta (tai null). Haku on rajattu
 * {@code <head>}iin: shell kirjoittaa metan sinne, ja rajaus takaa ettei
 * sisältöön mahdollisesti päätyvä tagi voi ohjata siltavalintaa — rendererit
 * escapoivat tekijäsisällön, joten tämä on vakuutus, ei reiän paikkaus.
 */
function readServingAppId(doc: Document | undefined): string | null {
  const id = readHeadMeta(doc, 'fasthub-app-id')?.trim();
  return id ? id : null;
}

/** Shellin `<head>`-metan arvo (tai null); sama head-rajaus kuin app-id:llä. */
function readHeadMeta(doc: Document | undefined, name: string): string | null {
  const head = doc?.head;
  if (!head || typeof head.querySelector !== 'function') return null;
  return head.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null;
}

/**
 * Onko dokumentti standalone-servingin shell (upotussivun-tapahtumaviesti-plan.md
 * WP-UV7)? Sama head-rajattu metan luku kuin siltavalinnalla, jotta kanavan
 * valinta ja muiden parent-kanavien kytkentä eivät voi ajautua erilleen.
 * Serving-dokumentin parent on upotettuna VIERAS sivu (WP-EM3), ei FastHub.
 */
export function isServingDocument(doc: Document | undefined): boolean {
  return readServingAppId(doc) != null;
}

/** postMessage-silta kehystetylle Apille (parent pitää tokenin). */
function createFramedClient(win: Window): BridgeClient {
  const parent = win.parent;
  const pending = new Map<string, Pending>();
  let counter = 0;

  // Korrelaatio-id: pelkkä laskuri (deterministinen; yksi client per iframe).
  const nextId = (): string => {
    counter += 1;
    return `inv-${counter}`;
  };

  win.addEventListener('message', (e: MessageEvent) => {
    // Hyväksy vain omalta parentilta (e.source-identiteetti).
    if (e.source !== parent) return;
    const data = e.data as { type?: string; id?: string; ok?: boolean; result?: unknown; error?: string };
    if (!data || data.type !== 'fasthub:app:response' || typeof data.id !== 'string') return;
    const p = pending.get(data.id);
    if (!p) return;
    pending.delete(data.id);
    clearTimeout(p.timer);
    if (data.ok && data.result !== undefined) {
      p.resolve(data.result);
    } else {
      p.reject(new Error(data.error || 'Data source request failed'));
    }
  });

  /**
   * Yksi korrelaatio-id:llinen pyyntö parentille. Kaikki kysyvät operaatiot
   * (invoke + käyttäjätoimet) kulkevat tästä, jotta niillä on sama aikakatko,
   * sama vastauskanava ja sama identiteettitarkistus — kolme rinnakkaista
   * toteutusta ajautuisi erilleen ensimmäisessä korjauksessa.
   */
  const request = <T>(message: Record<string, unknown>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const id = nextId();
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new Error('Data source request timed out'));
      }, REQUEST_TIMEOUT_MS);
      pending.set(id, { resolve: resolve as (r: unknown) => void, reject, timer });
      parent.postMessage({ ...message, id }, '*');
    });

  return {
    invoke(sourceId: string, payload?: unknown, query?: Record<string, unknown>): Promise<InvokeResult> {
      return request<InvokeResult>({ type: 'fasthub:app:invoke', sourceId, payload, query });
    },
    listUserActions(queue?: string, includeResolved?: boolean): Promise<unknown[]> {
      return request<unknown[]>({
        type: 'fasthub:app:user-actions:list',
        queue,
        includeResolved: includeResolved === true,
      });
    },
    respondUserAction(actionId: string, answer: UserActionAnswerPayload): Promise<unknown> {
      return request<unknown>({ type: 'fasthub:app:user-actions:respond', actionId, answer });
    },
    openLink(url: string, target: 'new' | 'same'): void {
      parent.postMessage({ type: 'fasthub:app:open-link', url, target }, '*');
    },
    openDocument(sourceId: string, payload?: unknown): void {
      parent.postMessage({ type: 'fasthub:app:open-document', sourceId, payload }, '*');
    },
    downloadDocument(sourceId: string, payload?: unknown): void {
      parent.postMessage({ type: 'fasthub:app:download-document', sourceId, payload }, '*');
    },
    saveText(filename: string, content: string, format: TextExportFormat): void {
      // Ei siivota tässä: sama työnjako kuin openLinkillä — parent on
      // auktoritatiivinen, koska vain se tietää mihin originiin blob syntyy.
      parent.postMessage({ type: 'fasthub:app:save-text', filename, content, format }, '*');
    },
    notifyEmbedder(): void {
      // D4 = A: kehystetty = portaalin runner tai editorin esikatselu, jonka
      // parent on FastHub eikä upottaja. Viestiä ei lähetetä minnekään —
      // varsinkaan '*'-kohteella, jolla tämä client muuten postaa.
    },
  };
}

/**
 * Standalone-servingin self-invoke-asiakas: kutsuu julkista datapolkua
 * same-origin-fetchillä. Ei tokenia (top-level-sivulla ei ole sitä) — palvelin
 * sallii vain julkisen Appin + julkisen reitin ja hylkää muut (401/403).
 */
function createServingClient(appId: string, win: Window): BridgeClient {
  const url = `/apps/${encodeURIComponent(appId)}/invoke`;
  const embedOrigins = parseEmbedOrigins(readHeadMeta(win.document, 'fasthub-embed-origins'));
  const client: BridgeClient = {
    invoke(sourceId: string, payload?: unknown, query?: Record<string, unknown>): Promise<InvokeResult> {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      return win
        .fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceId, payload, query }),
          signal: controller.signal,
        })
        .then(
          async (res) => {
            clearTimeout(timer);
            if (!res.ok) {
              // Endpoint-tason virhe (auth/allowlist/gateway) → virhe kuluttajalle.
              let message = 'Data source request failed';
              try {
                const err = await res.json();
                message = err?.message || err?.error || message;
              } catch {
                // ei JSON-runkoa
              }
              throw new Error(message);
            }
            const data = (await res.json()) as InvokeResult;
            return {
              statusCode: data.statusCode,
              responseBody: data.responseBody,
              durationMs: data.durationMs,
            };
          },
          (err: unknown) => {
            clearTimeout(timer);
            throw err instanceof Error ? err : new Error('Data source request failed');
          },
        );
    },
    /**
     * Serving-tilassa (top-level tai upotettu, WP-EM3) ei ole katsojaa: sivu
     * on julkinen eikä kanna tokenia, joten "minulle osoitetut toimet" ei
     * tarkoita mitään. Päätös `D4` = A — anonyymiä vastaamista ei tueta, koska
     * vastaus kirjataan vastaajan nimiin. Runtime näyttää tästä
     * kirjautumisohjeen.
     */
    listUserActions(): Promise<unknown[]> {
      return Promise.reject(new Error(NO_IDENTITY));
    },
    respondUserAction(): Promise<unknown> {
      return Promise.reject(new Error(NO_IDENTITY));
    },
    openLink(url: string, target: 'new' | 'same'): void {
      // Serving-tila on top-level-sivu TAI vieraan sivuston iframe (WP-EM3).
      // Avaus tehdään itse: FastHub-parenttia ei ole kummassakaan. Upotettuna
      // `_self` navigoi vain tämän kehyksen (ei isäntäsivua), ja sandboxattu
      // upotus tarvitsee `allow-popups`in — reunaehto on upotussopimuksessa
      // (SKILL.md). Sanitointi tehdään silti tässäkin: sama sääntö molemmilla
      // poluilla, eikä `javascript:` saa päätyä window.openiin.
      const safe = sanitizeUrl(url, 'link');
      if (!safe) return;
      win.open(safe, target === 'new' ? '_blank' : '_self', 'noopener,noreferrer');
    },
    openDocument(sourceId: string, payload?: unknown): void {
      // Serving-tilassa ei ole FastHub-parenttia eikä siis DocPreviewModalia
      // (top-level tai vieras upottaja, WP-EM3). Blob-URL:n saa avata itse —
      // mutta VAIN tyypeille jotka on turvallista renderöidä (blob perii tämän
      // originin); sandboxattu upotus tarvitsee `allow-popups`/`allow-downloads`in.
      // Muut ladataan; se on rehellisempi kuin avata tyhjä välilehti.
      void withDocument(sourceId, payload, (doc) => {
        const type = documentBlobType(doc.filename, doc.contentType);
        const url = URL.createObjectURL(new Blob([doc.bytes as BlobPart], { type }));
        if (isViewableInTab(type)) {
          win.open(url, '_blank', 'noopener,noreferrer');
          // Ei revokea heti: avattu välilehti lukee URL:n vasta latauduttuaan.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } else {
          saveBlobUrl(win, url, doc.filename);
        }
      });
    },
    downloadDocument(sourceId: string, payload?: unknown): void {
      void withDocument(sourceId, payload, (doc) => {
        const type = documentBlobType(doc.filename, doc.contentType);
        const url = URL.createObjectURL(new Blob([doc.bytes as BlobPart], { type }));
        saveBlobUrl(win, url, doc.filename);
      });
    },
    saveText(filename: string, content: string, format: TextExportFormat): void {
      // Serving-tilassa ei ole FastHub-parenttia (WP-EM3): sivu on top-level
      // (tai vieraan sivuston upotus) eikä sandboxattu, joten lataus onnistuu
      // tässä. Nimensiivous ja tyyppipakotus tehdään SILTI samoilla säännöillä
      // kuin parentilla — turvasääntö ei saa riippua siitä kumpi pää tekee työn.
      const safe = ensureExtension(safeFilename(filename), format);
      const url = URL.createObjectURL(new Blob([content], { type: textBlobType(format) }));
      saveBlobUrl(win, url, safe);
    },
    notifyEmbedder(message: EmbedderMessage): void {
      // Top-level-sivulla ei ole upottajaa. Kehystettynä parent on vieras sivu
      // (WP-EM3): kohteet valitsee embedderTargets (selaimen ilmoittama parentin
      // origin ensin, koko lista vain sen puuttuessa). Tyhjä lista (App ei
      // upotettavissa) → ei mitään. EI koskaan '*'.
      const parent = win.parent;
      if (!parent || parent === win) return;
      for (const origin of embedderTargets(embedOrigins, win.location?.ancestorOrigins)) {
        try {
          parent.postMessage(message, origin);
        } catch (e) {
          console.warn('[app-bridge] notify-embedder epäonnistui', origin, e);
        }
      }
    },
  };
  return client;

  /** Hae dokumentti datalähteestä ja anna se käsittelijälle; virhe konsoliin. */
  function withDocument(
    sourceId: string,
    payload: unknown,
    use: (doc: AppDocument) => void,
  ): Promise<void> {
    return client
      .invoke(sourceId, payload)
      .then((res) => {
        if (res.statusCode < 200 || res.statusCode >= 300) {
          throw new Error(`Datalähde vastasi ${res.statusCode}`);
        }
        use(parseAppDocument(res.responseBody));
      })
      .catch((e: unknown) => {
        console.warn('[app-bridge] dokumentin haku epäonnistui', e);
      });
  }
}

/** Tallenna blob-URL levylle ja vapauta se. */
function saveBlobUrl(win: Window, url: string, filename: string): void {
  const a = win.document.createElement('a');
  a.href = url;
  a.download = filename;
  win.document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
